/*
 * Author: Dw
 * File Created: Monday, 24-09-02 10:18 am
 * Last Modified: Tuesday, 25-03-25 08:09 pm, by Harper Yan
 */

import platform from 'platform';
import UAParser from 'ua-parser-js';

export async function collectDeviceInfo(isServer: boolean): Promise<string> {
  try {
    const parser = new UAParser();

    const deviceInfo = {
      userAgent: isServer ? undefined : navigator.userAgent, // 浏览器用户代理字符串
      platform: isServer ? platform.os?.family : navigator.platform, // 操作系统平台
      browser: parser.getBrowser(), // 浏览器信息（名称、版本）
      os: parser.getOS(), // 操作系统信息（名称、版本）
      device: parser.getDevice(), // 设备信息（型号、品牌、类型）
      cpu: parser.getCPU(), // CPU 信息（架构）
      screenResolution: isServer
        ? undefined
        : `${window.screen.width}x${window.screen.height}`, // 屏幕分辨率
      timezone: isServer
        ? undefined
        : Intl.DateTimeFormat().resolvedOptions().timeZone, // 时区信息
      language: isServer ? undefined : navigator.language || navigator.language, // 浏览器首选语言
      deviceMemory: isServer
        ? undefined
        : (navigator as any).deviceMemory || 'unknown', // 设备内存（GB）
      cpuCores: isServer
        ? undefined
        : navigator.hardwareConcurrency || 'unknown', // CPU 核心数
      networkType: isServer
        ? undefined
        : (navigator as any).connection?.effectiveType || 'unknown', // 网络连接类型（如 4g、wifi）
      online: isServer ? undefined : navigator.onLine, // 网络连接状态
      storageEstimate: isServer
        ? undefined
        : navigator.storage && navigator.storage.estimate
          ? await navigator.storage.estimate()
          : { quota: 'unknown', usage: 'unknown' }, // 存储空间（总量和已使用量）
      doNotTrack: isServer ? undefined : navigator.doNotTrack, // DNT（不追踪）设置
      cookiesEnabled: isServer ? undefined : navigator.cookieEnabled, // 是否启用 Cookie
      javaEnabled: isServer ? undefined : navigator.javaEnabled(), // 是否启用 Java
      environment: isServer ? 'server' : 'client', // 环境（区分客户端和服务端）
    };

    // 序列化为 JSON 字符串
    const deviceInfoString = JSON.stringify(deviceInfo);

    // 进行 base64 编码
    return Buffer.from(deviceInfoString).toString('base64');
  } catch (error) {
    console.error('Error collecting device information:', error);

    // 返回一个默认的编码字符串，避免影响流程
    return Buffer.from(
      JSON.stringify({
        error: 'Failed to collect device information',
        environment: isServer ? 'server' : 'client',
      })
    ).toString('base64');
  }
}

// Helper function to detect device type
export function detectDeviceType(): string {
  const parser = new UAParser();
  const osName = parser.getOS().name?.toLowerCase() || 'unknown';

  console.log('osName', osName);

  if (osName.includes('mac')) return 'mac';
  if (osName.includes('win')) return 'windows';
  if (osName.includes('android')) return 'android';
  if (
    osName.includes('ios') ||
    osName.includes('iphone') ||
    osName.includes('ipad')
  )
    return 'ios';

  return 'other';
}

export const getDeviceType = (): 'pc' | 'mobile' | 'tablet' | 'unknown' => {
  const parser = new UAParser();
  const result = parser.getResult();

  if (!result.device.type) {
    return 'pc'; // 如果 device.type 为 undefined，默认视为 PC 端
  }

  if (result.device.type === 'mobile') {
    return 'mobile';
  }

  if (result.device.type === 'tablet') {
    return 'tablet';
  }

  return 'unknown'; // 兜底逻辑
};

export interface UA {
  isAndroid: boolean;
  isIOS: boolean;
  isWindows: boolean;
  isMac: boolean;
  isPcMac: boolean;
  isIPad: boolean;
  isMobile: boolean;
  isWebKit: boolean;
  isChrome: boolean;
  isFirefox: boolean;
  isGecko: boolean;
  isIE: boolean;
  isEdge: boolean;
  isOpera: boolean;
  isSafari: boolean;
  isFacebook: boolean;
  isTwitter: boolean;
  isInstagram: boolean;
}

/**
 * generation ua object accroding to navigator.userAgent
 * @param {string} u navigator.userAgent
 * @return {null | Record<string, boolean>} ua object
 */

export function getUA(u: ''): null;
export function getUA(u: string): UA;
export function getUA(u: string): null | UA {
  if (!u) return null;

  function check(pattern: RegExp): boolean {
    return pattern.test(u);
  }

  const UAObject = {
    isAndroid: check(/android|linux|adr/i),
    isIOS: check(/\(i[^;]+;( U;)? CPU.+Mac OS X/i),
    isWindows: check(/window/i),
    isMac: check(/mac os x/i),
    isIPad: check(/iPad/i),
    isMobile: check(/AppleWebKit.*Mobile.*|Mobile/i),
    isWebKit: check(/webkit\W/i),

    isChrome: check(/webkit\W.*(chrome|chromium)\W/i),
    isFirefox: check(/mozilla.*\Wfirefox\W/i),
    isGecko: check(/mozilla(?!.*webkit).*\Wgecko\W/i),
    // eslint-disable-next-line func-names
    isIE: (function () {
      if (
        (typeof window !== 'undefined' &&
          navigator.appName === 'Microsoft Internet Explorer') ||
        check(/\bTrident\b/)
      ) {
        return true;
      }
      return false;
    })(),
    isEdge: check(/\bEdge\b/i),
    isOpera: check(/opera.*\Wpresto\W|OPR/i),
    isSafari: check(/webkit\W(?!.*chrome).*safari\W/i),

    isFacebook: check(/fban/i),
    isTwitter: check(/twitter/i),
    isInstagram: check(/instagram/i),
  } as UA;

  UAObject.isPcMac = !UAObject.isIOS && UAObject.isMac;

  return UAObject;
}

export default getUA;
